// src/components/certificado/CertificadoCard.js
//
// Tarjeta visual del certificado académico. Se renderiza en:
//   - La página pública de verificación (/cert/:codigo)
//   - "Mis certificados" del estudiante (modal de preview)
//   - El panel admin al previsualizar un certificado emitido
//
// Soporta 2 estilos predefinidos (Camino A):
//   - 'formal'  : certificado académico clásico, bordes dorados,
//                 tipografía serif, colores sobrios
//   - 'moderno' : versión contemporánea con banda de color y
//                 tipografía sans-serif
//
// Props:
//   cert              — datos del certificado (shape del backend)
//   plantilla         — datos de la plantilla elegida (color, logo, firma, firmante)
//   urlValidacion     — URL completa para el QR
//   institucionNombre — override opcional
//   mostrarQR         — bool, default true (en preview pequeño puede ocultarse)
//
// La tarjeta usa dimensiones A4 landscape (297×210 mm). Se renderiza
// escalable con `transform: scale()` desde el contenedor padre si hace
// falta encajar en el viewport del móvil.

import React, { forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';

// Diseño: usamos un ancho base de 1000px (ratio 1.414:1 ≈ A4 landscape).
// El contenedor padre decide con transform:scale() si lo achica para móvil.
const CERT_WIDTH = 1000;
const CERT_HEIGHT = Math.round(CERT_WIDTH / 1.414);  // 707px

// ─── Estilo FORMAL ─────────────────────────────────────────────────────
function FormalLayout({ cert, plantilla, urlValidacion, mostrarQR }) {
  const color = plantilla?.colorPrincipal || '#1a3d2e';
  return (
    <Box
      sx={{
        width: CERT_WIDTH, height: CERT_HEIGHT,
        bgcolor: '#fdfbf4',
        position: 'relative',
        fontFamily: '"Georgia", "Times New Roman", serif',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
      }}
    >
      {/* Doble marco */}
      <Box sx={{ position: 'absolute', inset: '18px', border: `3px solid ${color}` }} />
      <Box sx={{ position: 'absolute', inset: '28px', border: `1px solid ${color}99` }} />

      {/* Esquinas decorativas (adornos simples) */}
      {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((pos) => {
        const [v, h] = pos.split('-');
        return (
          <Box
            key={pos}
            sx={{
              position: 'absolute',
              [v]: 36, [h]: 36,
              width: 60, height: 60,
              borderTop:    v === 'top'    ? `3px solid ${color}` : 'none',
              borderBottom: v === 'bottom' ? `3px solid ${color}` : 'none',
              borderLeft:   h === 'left'   ? `3px solid ${color}` : 'none',
              borderRight:  h === 'right'  ? `3px solid ${color}` : 'none',
            }}
          />
        );
      })}

      {/* Contenido */}
      <Box sx={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'space-between',
        px: 10, py: 7,
      }}>
        {/* Encabezado */}
        <Box sx={{ textAlign: 'center' }}>
          {plantilla?.logoUrl && (
            <Box component="img" src={plantilla.logoUrl} alt="Logo"
                 sx={{ height: 80, mb: 2, objectFit: 'contain' }}
                 crossOrigin="anonymous" />
          )}
          <Typography sx={{ fontSize: 18, letterSpacing: 4, color, fontWeight: 500 }}>
            {(cert.institucionNombre || '').toUpperCase()}
          </Typography>
        </Box>

        {/* Centro */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 20, color: '#555', mb: 1, fontStyle: 'italic' }}>
            Certifica que
          </Typography>
          <Typography sx={{
            fontSize: 54, fontWeight: 700, color, mb: 2,
            fontFamily: '"Playfair Display", "Georgia", serif',
            borderBottom: `1px solid ${color}66`, pb: 1, px: 4,
          }}>
            {cert.estudianteNombre}
          </Typography>
          {cert.estudianteDocumento && (
            <Typography sx={{ fontSize: 15, color: '#666', mb: 3 }}>
              {cert.estudianteDocumento}
            </Typography>
          )}
          <Typography sx={{ fontSize: 20, color: '#444', mb: 1 }}>
            ha completado satisfactoriamente el curso
          </Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 600, color: '#222', mb: 2 }}>
            {cert.cursoTitulo}
          </Typography>
          {(cert.cursoDuracion || cert.calificacion != null) && (
            <Typography sx={{ fontSize: 15, color: '#666' }}>
              {cert.cursoDuracion}
              {cert.cursoDuracion && cert.calificacion != null && ' · '}
              {cert.calificacion != null && `Calificación: ${Number(cert.calificacion).toFixed(1)}`}
            </Typography>
          )}
        </Box>

        {/* Pie */}
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <Box sx={{ textAlign: 'center', minWidth: 260 }}>
            <Typography sx={{ fontSize: 13, color: '#666' }}>Fecha de emisión</Typography>
            <Typography sx={{ fontSize: 16, color: '#222', fontWeight: 500 }}>
              {formatearFecha(cert.fechaEmision)}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', minWidth: 260 }}>
            {plantilla?.firmaUrl && (
              <Box component="img" src={plantilla.firmaUrl} alt="Firma"
                   sx={{ height: 60, mb: -1, objectFit: 'contain' }}
                   crossOrigin="anonymous" />
            )}
            <Box sx={{ borderTop: '1px solid #999', pt: 0.5, mt: plantilla?.firmaUrl ? 0 : 5 }}>
              <Typography sx={{ fontSize: 14, color: '#222', fontWeight: 600 }}>
                {plantilla?.firmanteNombre || 'Dirección Académica'}
              </Typography>
              {plantilla?.firmanteCargo && (
                <Typography sx={{ fontSize: 12, color: '#666' }}>
                  {plantilla.firmanteCargo}
                </Typography>
              )}
            </Box>
          </Box>

          {mostrarQR && urlValidacion && (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ bgcolor: '#fff', p: 1, border: `1px solid ${color}66`, display: 'inline-block' }}>
                <QRCodeSVG value={urlValidacion} size={96} level="M" includeMargin={false} />
              </Box>
              <Typography sx={{ fontSize: 11, color: '#666', mt: 0.5, letterSpacing: 1 }}>
                Verificar · {cert.codigo}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Estilo MODERNO ────────────────────────────────────────────────────
function ModernoLayout({ cert, plantilla, urlValidacion, mostrarQR }) {
  const color = plantilla?.colorPrincipal || '#2563eb';
  return (
    <Box
      sx={{
        width: CERT_WIDTH, height: CERT_HEIGHT,
        bgcolor: '#ffffff', position: 'relative',
        fontFamily: '"Outfit", "Helvetica Neue", sans-serif',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
        display: 'grid', gridTemplateColumns: '240px 1fr',
      }}
    >
      {/* Banda lateral de color */}
      <Box sx={{
        bgcolor: color, color: '#fff',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'space-between',
        py: 6, px: 3, textAlign: 'center',
      }}>
        {plantilla?.logoUrl ? (
          <Box component="img" src={plantilla.logoUrl} alt="Logo"
               sx={{ maxHeight: 90, maxWidth: '100%', objectFit: 'contain',
                     filter: 'brightness(0) invert(1)' }}
               crossOrigin="anonymous" />
        ) : (
          <Typography sx={{ fontSize: 18, letterSpacing: 3, fontWeight: 300 }}>
            CERTIFICADO
          </Typography>
        )}

        <Box>
          <Typography sx={{ fontSize: 11, letterSpacing: 2, opacity: 0.8 }}>EMITIDO POR</Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 0.5, lineHeight: 1.3 }}>
            {cert.institucionNombre}
          </Typography>
        </Box>

        {mostrarQR && urlValidacion && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ bgcolor: '#fff', p: 0.8, display: 'inline-block', borderRadius: 1 }}>
              <QRCodeSVG value={urlValidacion} size={90} level="M" includeMargin={false} />
            </Box>
            <Typography sx={{ fontSize: 10, mt: 0.5, letterSpacing: 1, opacity: 0.9 }}>
              {cert.codigo}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Contenido principal */}
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between',
        px: 7, py: 6,
      }}>
        <Box>
          <Typography sx={{ fontSize: 13, letterSpacing: 4, color, fontWeight: 600 }}>
            CERTIFICADO DE FINALIZACIÓN
          </Typography>
          <Box sx={{ width: 60, height: 3, bgcolor: color, mt: 1 }} />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 17, color: '#555', mb: 1.5 }}>
            Se otorga el presente certificado a
          </Typography>
          <Typography sx={{
            fontSize: 48, fontWeight: 700, color: '#111', lineHeight: 1.1, mb: 1,
          }}>
            {cert.estudianteNombre}
          </Typography>
          {cert.estudianteDocumento && (
            <Typography sx={{ fontSize: 14, color: '#777', mb: 2 }}>
              Documento: {cert.estudianteDocumento}
            </Typography>
          )}
          <Typography sx={{ fontSize: 16, color: '#555', mb: 0.5 }}>
            por haber completado satisfactoriamente el curso
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 600, color, mb: 1.5 }}>
            {cert.cursoTitulo}
          </Typography>
          {(cert.cursoDuracion || cert.calificacion != null) && (
            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
              {cert.cursoDuracion && (
                <Box>
                  <Typography sx={{ fontSize: 11, color: '#888', letterSpacing: 1 }}>DURACIÓN</Typography>
                  <Typography sx={{ fontSize: 15, color: '#222', fontWeight: 500 }}>{cert.cursoDuracion}</Typography>
                </Box>
              )}
              {cert.calificacion != null && (
                <Box>
                  <Typography sx={{ fontSize: 11, color: '#888', letterSpacing: 1 }}>CALIFICACIÓN</Typography>
                  <Typography sx={{ fontSize: 15, color: '#222', fontWeight: 500 }}>
                    {Number(cert.calificacion).toFixed(1)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 4 }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: '#888', letterSpacing: 1 }}>FECHA DE EMISIÓN</Typography>
            <Typography sx={{ fontSize: 15, color: '#222', fontWeight: 500 }}>
              {formatearFecha(cert.fechaEmision)}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', minWidth: 260 }}>
            {plantilla?.firmaUrl && (
              <Box component="img" src={plantilla.firmaUrl} alt="Firma"
                   sx={{ height: 60, mb: -1, objectFit: 'contain' }}
                   crossOrigin="anonymous" />
            )}
            <Box sx={{ borderTop: '1px solid #999', pt: 0.5, mt: plantilla?.firmaUrl ? 0 : 5 }}>
              <Typography sx={{ fontSize: 14, color: '#222', fontWeight: 600 }}>
                {plantilla?.firmanteNombre || 'Dirección Académica'}
              </Typography>
              {plantilla?.firmanteCargo && (
                <Typography sx={{ fontSize: 12, color: '#666' }}>
                  {plantilla.firmanteCargo}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Dispatcher ────────────────────────────────────────────────────────
const CertificadoCard = forwardRef(function CertificadoCard(
  { cert, plantilla, urlValidacion, mostrarQR = true }, ref
) {
  if (!cert) return null;
  const estilo = plantilla?.estilo || 'formal';
  const Layout = estilo === 'moderno' ? ModernoLayout : FormalLayout;
  return (
    <Box ref={ref} sx={{ display: 'inline-block' }}>
      <Layout cert={cert} plantilla={plantilla} urlValidacion={urlValidacion} mostrarQR={mostrarQR} />
    </Box>
  );
});

export default CertificadoCard;

// ─── Utilidades ────────────────────────────────────────────────────────
function formatearFecha(fecha) {
  if (!fecha) return '';
  try {
    const d = new Date(fecha);
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
  } catch { return String(fecha); }
}

// Helpers de descarga/impresión — mismo patrón que FENAGRO
export async function descargarCertificadoPng(cardEl, codigo) {
  try {
    const html2canvasMod = await import('html2canvas');
    const html2canvas = html2canvasMod.default || html2canvasMod;
    const canvas = await html2canvas(cardEl, {
      scale: 2, backgroundColor: null, useCORS: true, allowTaint: true,
    });
    const link = document.createElement('a');
    link.download = `certificado_${codigo}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (err) {
    imprimirCertificado(cardEl, codigo);
  }
}

export function imprimirCertificado(cardEl, codigo) {
  const printWindow = window.open('', '_blank', 'width=1200,height=850');
  if (!printWindow) return;
  const html = cardEl?.outerHTML || '';
  printWindow.document.write(`
    <html>
      <head>
        <title>Certificado ${codigo}</title>
        <style>
          body { margin: 0; padding: 30px; background: #f5f5f5; }
          @media print {
            body { background: #fff; padding: 0; }
            @page { size: A4 landscape; margin: 0; }
          }
        </style>
      </head>
      <body>${html}</body>
    </html>
  `);
  printWindow.document.close();
  setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
}
